import { useCallback, useEffect, useRef, useState } from "react";
import { useBooks } from "./BooksProvider";

export interface Loaded<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** Re-runs the read; resolves once it has landed. */
  reload: () => Promise<void>;
}

/**
 * One read per screen: `read` returns openapi-fetch's `{ data, error }`. `notFound` turns a 404 into data
 * (GET /books/current answers 404 when the organisation has never had a book).
 */
export function useLoad<T>(
  read: () => Promise<{ data?: T; error?: unknown; response: Response }>,
  deps: unknown[],
  options: { enabled?: boolean; notFound?: T } = {},
): Loaded<T> {
  const { enabled = true } = options;
  // Re-read whenever the host says the books may have changed elsewhere (an agent turn).
  const { changes } = useBooks();
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(enabled);
  const readRef = useRef(read);
  readRef.current = read;
  const notFoundRef = useRef(options.notFound);
  notFoundRef.current = options.notFound;
  const latest = useRef(0);

  const reload = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    try {
      const { data, error, response } = await readRef.current();
      if (call !== latest.current) return;
      if (response.status === 404 && "notFound" in options) {
        setData(notFoundRef.current);
        setError(undefined);
      } else {
        setData(error ? undefined : data);
        setError(error);
      }
    } catch (e) {
      if (call === latest.current) setError(e);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [...deps, changes]);

  // New inputs (another period, page or filter) drop the old answer, so a slow read never shows the previous one as
  // this one's. A refresh in place (`changes`, `reload`) keeps it on screen while the new one loads.
  useEffect(() => {
    setData(undefined);
    setError(undefined);
  }, deps);

  useEffect(() => {
    if (enabled) void reload();
    else setLoading(false);
  }, [reload, enabled]);

  // Back on the tab: something may have posted meanwhile (a payment on another tab, a bank sync), so read again in place.
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload, enabled]);

  return { data, error, loading, reload };
}
